import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Center, Flex, Grid } from '@chakra-ui/react';
import { useT, hasValue } from '@progress-fe/core';
import { v4 as uuidv4 } from 'uuid';
import { Textarea } from '../../inputs';
import { TreeListDialog } from '../../dialogs';
import { useJsFieldFormData } from '../../../hooks';
import { JsFieldError, JsFieldInspector, JsFormGroupName } from '../../jsFormBase';
import { JsFormConfigContext } from '../../../contexts';
import { FunctionParam } from './components';
/**
 * Component renders "/schemas/jsf-function" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TFormData.
 */
const FunctionJsFieldFC = (props) => {
    const [isDialog, setIsDialog] = useState(null);
    const { trees = [], isInspector } = useContext(JsFormConfigContext);
    const { t } = useT();
    const formData = useMemo(() => (hasValue(props.formData) ? props.formData : null), [props.formData]);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: formData?.fieldConfig ?? null,
        values: formData?.fieldValues ?? null,
        onChange: props.onChange,
        watchPropsChanges: true
    });
    const handleChangeFunc = useCallback((func) => {
        if (fieldValues) {
            onChangeFieldValues({ ...fieldValues, func });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleAddParam = useCallback((param) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                inputParams: [...fieldValues.inputParams, param]
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeParamJsonPath = useCallback((uuid, name, value, elementUuid, entityType, jsonPath) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                inputParams: fieldValues.inputParams.map((param) => {
                    return param.uuid === uuid
                        ? { ...param, name, elementUuid, jsonPath, value: value ?? null }
                        : param;
                })
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeParamName = useCallback((uuid, name) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                inputParams: fieldValues.inputParams.map((param) => ({
                    ...param,
                    name: param.uuid === uuid ? name : param.name
                }))
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleChangeParamValue = useCallback((uuid, value) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                inputParams: fieldValues.inputParams.map((param) => ({
                    ...param,
                    value: param.uuid === uuid ? value : param.value
                }))
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    const handleDeleteParam = useCallback((uuid) => {
        if (fieldValues) {
            onChangeFieldValues({
                ...fieldValues,
                inputParams: fieldValues.inputParams.filter((p) => p.uuid !== uuid)
            });
        }
    }, [fieldValues, onChangeFieldValues]);
    useEffect(() => {
        onChangeFieldConfig(formData?.fieldConfig);
    }, [formData?.fieldConfig, onChangeFieldConfig]);
    const hasParams = (fieldValues?.inputParams ?? []).length > 0;
    if (!fieldConfig || !fieldValues) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(Box, { "data-testid": "FunctionJsField-test", "data-name": props.name, children: [_jsx(Textarea, { isOnChangeOnlyOnBlur: true, value: fieldValues.func, disabled: props.schema.readOnly, onChange: handleChangeFunc }), _jsx(JsFormGroupName, { name: t('fields.inputParams') }), _jsxs(Flex, { p: '0 0 2px 0', flexDirection: 'column', gap: '8px', children: [hasParams ? (_jsx(Flex, { gap: '4px', flexDirection: 'column', children: fieldValues.inputParams.map((inputParam) => (_jsx(FunctionParam, { inputParam: inputParam, isSchemaReadonly: props.schema.readOnly, onChangeName: (v) => handleChangeParamName(inputParam.uuid, v), onChangeValue: (v) => handleChangeParamValue(inputParam.uuid, v), onDeleteParam: () => handleDeleteParam(inputParam.uuid), onLinkClick: () => setIsDialog({ uuid: inputParam.uuid }) }, inputParam.uuid))) })) : (_jsx(Center, { h: '32px', color: 'bodyText', border: '1px solid', borderColor: 'border', borderRadius: '4px', children: _jsx(Box, { opacity: "0.6", children: t('common.noParams') }) })), !props.schema.readOnly && (_jsxs(Grid, { gridTemplateColumns: '1fr 1fr', gap: '4px', children: [_jsx(Button, { size: 'xs', variant: 'ghost', onClick: () => handleAddParam({ uuid: uuidv4(), name: '', value: '' }), children: t('actions.add') }), _jsx(Button, { size: 'xs', variant: 'ghost', onClick: () => setIsDialog({ uuid: null }), children: t('actions.addFrom') })] }))] }), isDialog && !hasValue(isDialog.uuid) && (_jsx(TreeListDialog, { trees: trees, title: t('common.chooseProp'), onClose: () => setIsDialog(null), onClickTreeItem: (treeId, _, treeType, data) => {
                        handleAddParam({
                            uuid: uuidv4(),
                            name: data.name ?? '',
                            value: hasValue(data.value) ? data.value.toString() : null,
                            jsonPath: data.path ?? undefined,
                            entityUuid: treeId,
                            entityType: treeType
                        });
                        setIsDialog(null);
                    } })), isDialog && hasValue(isDialog.uuid) && (_jsx(TreeListDialog, { trees: trees, title: t('common.chooseProp'), onClose: () => setIsDialog(null), activeItems: fieldValues.inputParams
                        ?.filter((ip) => ip.uuid === isDialog.uuid && hasValue(ip.jsonPath))
                        .map((ip) => ({
                        uuid: ip.uuid,
                        treeId: ip.entityUuid ?? '',
                        jsonPath: ip.jsonPath ?? ''
                    })), onClickTreeItem: (treeId, treeName, treeType, data) => {
                        const foundParam = fieldValues.inputParams?.find((ip) => ip.uuid === isDialog.uuid);
                        if (isDialog.uuid && data.path && foundParam) {
                            const treeValue = hasValue(data.value) ? data.value.toString() : '';
                            const paramValue = hasValue(foundParam.value) ? foundParam.value.toString() : '';
                            // Clear or update a jsonPath
                            if (foundParam.jsonPath === data.path) {
                                handleChangeParamJsonPath(isDialog.uuid, foundParam.name ?? '', paramValue);
                            }
                            else {
                                handleChangeParamJsonPath(isDialog.uuid, foundParam.name ?? '', treeValue, treeId, treeType, data.path);
                            }
                            setIsDialog(null);
                        }
                    } }))] }) }));
};
export const FunctionJsField = FunctionJsFieldFC;
//# sourceMappingURL=FunctionJsField.js.map